import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface GalleryItem {
  id: number;
  image_url: string;
  thumbnail_url?: string;
}

interface ImageGallery {
  id: number;
  title: string;
  description?: string;
  cover_image?: string;
  category?: string;
  items?: GalleryItem[];
  items_count?: number;
}

const PublicImageGalleries: React.FC = () => {
  const [galleries, setGalleries] = useState<ImageGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cms/image-galleries');
      // Get only the latest 6 galleries
      const latestGalleries = response.data.slice(0, 6);
      setGalleries(latestGalleries);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner"></div>
        <p>{t('public.galleries.loading')}</p>
      </div>
    );
  }

  if (galleries.length === 0) {
    return null; // Don't show section if no galleries
  }

  return (
    <section className="galleries-section">
      <div className="section-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div>
          <h2>{t('public.galleries.latestTitle')}</h2>
          <p style={{ 
            fontSize: '1rem', 
            color: '#666',
            margin: '0.5rem 0 0 0'
          }}>
            {t('public.galleries.homeDescription')}
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/galleries')}
          style={{
            padding: '12px 30px',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          {t('public.galleries.viewAll')} <i className="fas fa-arrow-right" style={{ marginLeft: '8px' }}></i>
        </button>
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '2rem'
      }}>
        {galleries.map((gallery) => {
          const imageCount = gallery.items?.length || gallery.items_count || 0;
          const coverImage = gallery.cover_image || gallery.items?.[0]?.thumbnail_url || gallery.items?.[0]?.image_url;
          
          return (
            <div 
              key={gallery.id}
              onClick={() => navigate(`/galleries/${gallery.id}`)}
              style={{
                background: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                position: 'relative',
                paddingBottom: '75%',
                height: 0,
                overflow: 'hidden',
                background: '#f0f0f0'
              }}>
                {coverImage ? (
                  <img 
                    src={coverImage} 
                    alt={gallery.title}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#999',
                    fontSize: '48px'
                  }}>
                    <i className="fas fa-images"></i>
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  bottom: '15px',
                  right: '15px',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: '#fff',
                  padding: '8px 15px',
                  borderRadius: '20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i className="fas fa-images"></i>
                  {imageCount}
                </div>
              </div>
              <div style={{ padding: '20px' }}>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#333',
                  lineHeight: '1.4'
                }}>
                  {gallery.title}
                </h3>
                {gallery.description && (
                  <p style={{ 
                    margin: 0, 
                    fontSize: '14px', 
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.5'
                  }}>
                    {gallery.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PublicImageGalleries;

